/*
Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained
access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.
*/

import {
  Button,
  Checkbox,
  FormControlLabel,
  TextField,
  Snackbar,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  IconButton,
} from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import Styles from '../../css/OneTimePaymentPage.module.css';
import { useOneTimePaymentContext } from '../../contexts/OneTimePaymentContext';
import { useAuthContext } from '../../contexts/AuthContext';
import { useRootContext } from '../../contexts/RootContext';
import OneTimePaymentModal from '../modals/OneTimePaymentModal';
import { NavLink } from 'react-router-dom';
import { NumericFormat, PatternFormat } from 'react-number-format';
import {
  convertDateInputTo8AMUTC,
  formatDateYYYYMMDD,
  isStrEmptyOrNull,
  isValidRoutingNumber,
} from '../../common/UtilityFunctions';
import LoadingModal from '../modals/LoadingModal';
import EscrowShortageModal from '../modals/EscrowShortageModal';
import { numericFormatter } from 'react-number-format';
import ButtonBorder from '../ButtonBorder';
import LaunchIcon from '@mui/icons-material/Launch';
import EnvelopeWithBox from '../EnvelopeWithBox';
import EnvelopeStyles from '../../css/EnvelopeWithBox.module.css';
import ButtonBorderWrapper from '../ButtonBorderWrapper';
import { useViewingContext } from '../../contexts/ViewingContext';
import { InfoOutlined } from '@mui/icons-material';
import React from 'react';
import useWindowDimensions from '../../common/hooks/useWindowDimensions';

const COMPACT_FORM_MAX_WIDTH = 1024;

const OneTimePaymentForm = () => {
  const oneTimePaymentContext = useOneTimePaymentContext();
  const rootContext = useRootContext();
  const { isServicerView } = useViewingContext();
  const { innerWidth } = useWindowDimensions();
  const isCompactFormLayout = innerWidth <= COMPACT_FORM_MAX_WIDTH;
  const useTapLanguage = isCompactFormLayout;
  const pointerVerb = useTapLanguage ? 'Tap' : 'Click';
  const { servicerUser, fullAccess } = useAuthContext();
  dayjs.extend(utc);
  dayjs.extend(timezone);

  const submitDisabled =
    oneTimePaymentContext.pageError ||
    oneTimePaymentContext.hasDisablingStopCode ||
    oneTimePaymentContext.hasDisablingStatus ||
    oneTimePaymentContext.isValidatingPayment ||
    (!fullAccess && isServicerView);

  const showRemoveBankDetails =
    oneTimePaymentContext.originalBankingDetails &&
    (oneTimePaymentContext.originalBankingDetails.otpBankAccountNo ||
      oneTimePaymentContext.originalBankingDetails.otpBankAccountTypeKeyword ||
      oneTimePaymentContext.originalBankingDetails.otpBankName ||
      oneTimePaymentContext.originalBankingDetails.otpRoutingNo);

  const renderSubmitButton = () => (
    <Button
      disabled={submitDisabled}
      variant='contained'
      type='submit'
      className={Styles.button}
      onClick={oneTimePaymentContext.validateFields}
    >
      Submit
    </Button>
  );

  const renderRemoveBankDetailsButton = () =>
    showRemoveBankDetails ? (
      <Button
        variant='contained'
        className={Styles.cancelButton}
        onClick={() => {
          oneTimePaymentContext.setOneTimePaymentModalContent('removeBankDetails');
          oneTimePaymentContext.setOneTimePaymentModalOpen(true);
        }}
      >
        Remove Saved Bank Details
      </Button>
    ) : null;

  const renderServicerDisclosure = () =>
    isCompactFormLayout ? (
      <div className={`${Styles.flexColServicer} ${Styles.businessText}`}>
        <h3 className={Styles.businessHeader}>Check by Phone Disclosure</h3>
        <div className={Styles.textSection}>
          <p className={Styles.preLineText}>
            Do you certify that you are an authorized signer on the bank account you have provided today?
          </p>
          <p className={Styles.boldText}>
            After the borrower confirms that he/she is an authorized signer on the bank account, read the following
            disclosure:
          </p>
          <p className={Styles.preLineText}>
            {
              'If the account information provided is not accurate or if this payment gets returned by your bank as "unable to locate", "invalid account number", insufficient or uncollected funds, Sun West Mortgage will not be held responsible for any fee(s) assessed or negative credit reporting, and all future dated electronic payments may be deleted and not processed.\n\nDo you authorize the payment of <$0.00> for <Date of Payment> from <Bank Name> from your bank account ending in <Last 4 Digits of Checking/Savings Account Number>'
            }
          </p>
          <p className={Styles.preLineText}>
            <NavLink
              to='/faq'
              rel='noopener noreferrer'
              onClick={(e) => {
                e.preventDefault();
                window.open('https://www.swmc.com/faq', '_blank', 'noopener,noreferrer');
              }}
            >
              Borrower Online Payment Instructions and FAQ <LaunchIcon className={Styles.externalIcon} />
            </NavLink>
          </p>
        </div>
      </div>
    ) : (
      <div>
        <h3>Check by Phone Disclosure</h3>
        <p className={Styles.text}>
          {'\nDo you certify that you are an authorized signer on the bank account you have provided today?\n\n'}
        </p>
        <p>
          After the borrower confirms that he/she is an authorized signer on the bank account, read the following
          disclosure:
        </p>
        <p className={Styles.text}>
          {'\nIf the account information provided is not accurate or if this payment gets returned by your bank as "unable to locate", ' +
            '"invalid account number", insufficient or uncollected funds, Sun West Mortgage will not be held responsible for any fee(s) ' +
            'assessed or negative credit reporting, and all future dated electronic payments may be deleted and not processed.\n\n' +
            'Do you authorize the payment of <$0.00> for <Date of Payment> from <Bank Name> from your bank account ending in ' +
            '<Last 4 Digits of Checking/Savings Account Number>\n\n'}
        </p>
        <p className={Styles.text}>
          <NavLink
            to='/faq'
            rel='noopener noreferrer'
            onClick={(e) => {
              e.preventDefault();
              window.open('https://www.swmc.com/faq', '_blank', 'noopener,noreferrer');
            }}
          >
            Borrower Online Payment Instructions and FAQ <LaunchIcon className={Styles.externalIcon} />
          </NavLink>
          {'\n\n'}
        </p>
      </div>
    );

  return (
    <ButtonBorderWrapper className={Styles.formWrapper}>
      <EnvelopeWithBox
        blockName='Make an On-Line Electronic Payment'
        className={`${Styles.otpEnvelope} ${EnvelopeStyles.tabTitleWrap}`}
      >
        <div className={Styles.page}>
          {oneTimePaymentContext.oneTimePaymentModalOpen && (
            <OneTimePaymentModal content={oneTimePaymentContext.oneTimePaymentModalContent} />
          )}
          {oneTimePaymentContext.escrowShortageModalOpen && <EscrowShortageModal />}
          {oneTimePaymentContext.status === 'loading' && (
            <LoadingModal
              open={oneTimePaymentContext.status === 'loading'}
              loadingMessage={oneTimePaymentContext.loadingMessage}
            />
          )}
          <Snackbar
            open={!!oneTimePaymentContext.statusAlert.message}
            onClose={() =>
              oneTimePaymentContext.setStatusAlert({
                status: '',
                message: '',
              })
            }
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            ClickAwayListenerProps={{ onClickAway: () => null }}
          >
            <Alert
              onClose={() =>
                oneTimePaymentContext.setStatusAlert({
                  status: '',
                  message: '',
                })
              }
              severity={oneTimePaymentContext.statusAlert.status as any}
              variant='filled'
              sx={{ minWidth: '30vw' }}
            >
              {oneTimePaymentContext.statusAlert.message}
            </Alert>
          </Snackbar>
          <div className={Styles.flexCol}>
            {oneTimePaymentContext.hasFuturePayment && (
              <p className={Styles.warningText}>
                Warning: One Time Payment is pending or already transferred to the bank.
              </p>
            )}
            {oneTimePaymentContext.hasAutopay && (
              <p className={Styles.warningText}>Warning: Loan has recurring EFT payment setup.</p>
            )}
            {!isServicerView && oneTimePaymentContext.hasDisablingStopCode && (
              <p className={Styles.warningText}>
                <IconButton
                  className={Styles.icon}
                  onClick={() => {
                    if (oneTimePaymentContext.stopCodeST02) {
                      oneTimePaymentContext.setOneTimePaymentModalContent('stopCodeST02');
                    } else if (oneTimePaymentContext.stopCodeST03) {
                      oneTimePaymentContext.setOneTimePaymentModalContent('stopCodeST03');
                    } else if (oneTimePaymentContext.stopCodeST20) {
                      oneTimePaymentContext.setOneTimePaymentModalContent('stopCodeST20');
                    }
                    oneTimePaymentContext.setOneTimePaymentModalOpen(true);
                  }}
                >
                  <InfoOutlined className={Styles.icon} />
                </IconButton>
                One Time Payment cannot be set up.
              </p>
            )}
            {isServicerView && oneTimePaymentContext.stopCodeST02 && (
              <p className={Styles.warningText}>
                <IconButton
                  className={Styles.icon}
                  onClick={() => {
                    oneTimePaymentContext.setOneTimePaymentModalContent('stopCodeST02');
                    oneTimePaymentContext.setOneTimePaymentModalOpen(true);
                  }}
                >
                  <InfoOutlined className={Styles.icon} />
                </IconButton>
                ST02: Certified Funds Freeze (NSF) - One Time Payment cannot be set up.
              </p>
            )}
            {isServicerView && oneTimePaymentContext.stopCodeST03 && (
              <p className={Styles.warningText}>
                <IconButton
                  className={Styles.icon}
                  onClick={() => {
                    oneTimePaymentContext.setOneTimePaymentModalContent('stopCodeST03');
                    oneTimePaymentContext.setOneTimePaymentModalOpen(true);
                  }}
                >
                  <InfoOutlined className={Styles.icon} />
                </IconButton>
                ST03: Complete Freeze - One Time Payment cannot be set up.
              </p>
            )}
            {isServicerView && oneTimePaymentContext.stopCodeST20 && (
              <p className={Styles.warningText}>
                <IconButton
                  className={Styles.icon}
                  onClick={() => {
                    oneTimePaymentContext.setOneTimePaymentModalContent('stopCodeST20');
                    oneTimePaymentContext.setOneTimePaymentModalOpen(true);
                  }}
                >
                  <InfoOutlined className={Styles.icon} />
                </IconButton>
                ST20: Certified Funds Freeze (Notice of Intent to Foreclosure) - One Time Payment cannot be set up.
              </p>
            )}
            {!isServicerView && oneTimePaymentContext.hasDisablingStatus && (
              <p className={Styles.warningText}>
                <IconButton
                  className={Styles.icon}
                  onClick={() => {
                    if (oneTimePaymentContext.statusForeclosure) {
                      oneTimePaymentContext.setOneTimePaymentModalContent('statusForeclosure');
                    } else if (oneTimePaymentContext.statusDeedInLieu) {
                      oneTimePaymentContext.setOneTimePaymentModalContent('statusDeedInLieu');
                    } else if (oneTimePaymentContext.statusShortSale) {
                      oneTimePaymentContext.setOneTimePaymentModalContent('statusShortSale');
                    } else if (oneTimePaymentContext.statusServicingTransfer) {
                      oneTimePaymentContext.setOneTimePaymentModalContent('statusServicingTransfer');
                    }
                    oneTimePaymentContext.setOneTimePaymentModalOpen(true);
                  }}
                >
                  <InfoOutlined className={Styles.icon} />
                </IconButton>
                One Time Payment cannot be set up.
              </p>
            )}
            {isServicerView && oneTimePaymentContext.statusForeclosure && (
              <p className={Styles.warningText}>
                <IconButton
                  className={Styles.icon}
                  onClick={() => {
                    oneTimePaymentContext.setOneTimePaymentModalContent('statusForeclosure');
                    oneTimePaymentContext.setOneTimePaymentModalOpen(true);
                  }}
                >
                  <InfoOutlined className={Styles.icon} />
                </IconButton>
                Loan Status: Foreclosure - One Time Payment cannot be set up.
              </p>
            )}
            {isServicerView && oneTimePaymentContext.statusDeedInLieu && (
              <p className={Styles.warningText}>
                <IconButton
                  className={Styles.icon}
                  onClick={() => {
                    oneTimePaymentContext.setOneTimePaymentModalContent('statusDeedInLieu');
                    oneTimePaymentContext.setOneTimePaymentModalOpen(true);
                  }}
                >
                  <InfoOutlined className={Styles.icon} />
                </IconButton>
                Loan Status: Deed-In-Lieu - One Time Payment cannot be set up.
              </p>
            )}
            {isServicerView && oneTimePaymentContext.statusShortSale && (
              <p className={Styles.warningText}>
                <IconButton
                  className={Styles.icon}
                  onClick={() => {
                    oneTimePaymentContext.setOneTimePaymentModalContent('statusShortSale');
                    oneTimePaymentContext.setOneTimePaymentModalOpen(true);
                  }}
                >
                  <InfoOutlined className={Styles.icon} />
                </IconButton>
                Loan Status: Short Sale - One Time Payment cannot be set up.
              </p>
            )}
            {isServicerView && oneTimePaymentContext.statusServicingTransfer && (
              <p className={Styles.warningText}>
                <IconButton
                  className={Styles.icon}
                  onClick={() => {
                    oneTimePaymentContext.setOneTimePaymentModalContent('statusServicingTransfer');
                    oneTimePaymentContext.setOneTimePaymentModalOpen(true);
                  }}
                >
                  <InfoOutlined className={Styles.icon} />
                </IconButton>
                Loan Status: Servicing Transfer - One Time Payment cannot be set up.
              </p>
            )}
            {isServicerView && renderServicerDisclosure()}
            {!isServicerView && (
              <p className={Styles.text}>
                {'This one time payment must be entered by 10:00PM PT, MONDAY THROUGH FRIDAY, to be considered received today. Payments submitted' +
                  ' after 10:00PM PT or on a non-business day, may be posted to your account the next business day.\n\n' +
                  'Please note that if this check is returned because of insufficient funds or an incorrect account number you will be charged' +
                  ' a $15.00 dishonored check fee. If you are using a Credit Union account, the information you input may be different. Please check' +
                  ' with your financial institution to verify both the routing number and account number prior to using this service for the first time.\n\n' +
                  'When you input your account number, input just the numbers and leave out any special characters like #, - etc. Do not use the number' +
                  ' from your check card or ATM. Unlike an ATM transaction, an electronic check does not receive immediate approval from your bank.' +
                  ' Your transaction can take up to 72 hours to reach your bank account. However, you should not approve this transaction unless the' +
                  ' funds are available in your account now.\n\n' +
                  'Important: Do not attempt to use credit card cash advance checks, brokerage account checks, or any checks marked do not use for ACH.' +
                  ' Your debit or credit cards will not work. You will need to enter your bank account number. Only checks from regular checking at US' +
                  ' domestic banks, including most credit unions may be used for electronic check payments. Be sure to copy the routing/transit number' +
                  ' and account numbers very carefully from your check. If you enter incorrect values or attempt to use a check that is not from a' +
                  ' regular US domestic bank checking account, your electronic check will be returned.\n\n' +
                  'If you are unsure of whether or not your check can be used or what routing/transit and account numbers to enter, call your bank,' +
                  ' ask them if your account can be used for ACH, and verify the correct numbers to use.\n\n'}
                {pointerVerb}{' '}
                <NavLink
                  to='/faq'
                  rel='noopener noreferrer'
                  onClick={(e) => {
                    e.preventDefault();
                    window.open('https://www.swmc.com/faq', '_blank', 'noopener,noreferrer');
                  }}
                >
                  here <LaunchIcon className={Styles.externalIcon} />
                </NavLink>{' '}
                {'for instructions on How to Make Additional Payments Online.\n\n'}
              </p>
            )}
            <h3 className={Styles.infoLabel}>Please complete the following information:</h3>
            <form onSubmit={oneTimePaymentContext.validateFields}>
              <div className={Styles.gridContainer}>
                <div>
                  <PatternFormat
                    fullWidth
                    size='small'
                    label='Transit Routing No *'
                    placeholder='Transit Routing No *'
                    customInput={TextField}
                    format='#########'
                    inputProps={{ inputMode: 'numeric' }}
                    error={oneTimePaymentContext.otpRoutingNoError}
                    helperText={oneTimePaymentContext.otpRoutingNoError ? 'Invalid Routing Number' : ''}
                    value={oneTimePaymentContext.autopayInfo?.otpRoutingNo || ''}
                    onChange={(e) => {
                      oneTimePaymentContext.dispatchAutopayInfo({
                        type: 'changed_property',
                        property: { otpRoutingNo: e.target.value },
                      });
                      if (oneTimePaymentContext.otpRoutingNoError && isValidRoutingNumber(e.target.value)) {
                        oneTimePaymentContext.setOtpRoutingNoError(false);
                      }
                    }}
                  />
                  <Button className={Styles.helpButton} onClick={rootContext.scrollToElement}>
                    Help
                  </Button>
                </div>
                <TextField
                  fullWidth
                  size='small'
                  label='Bank Name *'
                  placeholder='Bank Name *'
                  error={oneTimePaymentContext.otpBankNameError}
                  helperText={oneTimePaymentContext.otpBankNameError ? 'Bank Name Required' : ''}
                  value={oneTimePaymentContext.autopayInfo?.otpBankName || ''}
                  onChange={(e) => {
                    oneTimePaymentContext.dispatchAutopayInfo({
                      type: 'changed_property',
                      property: { otpBankName: e.target.value },
                    });
                    if (oneTimePaymentContext.otpBankNameError && !isStrEmptyOrNull(e.target.value)) {
                      oneTimePaymentContext.setOtpBankNameError(false);
                    }
                  }}
                />
                <div>
                  <TextField
                    fullWidth
                    size='small'
                    label='Bank Account No'
                    placeholder='Bank Account No *'
                    error={oneTimePaymentContext.otpBankAccountNoError}
                    helperText={oneTimePaymentContext.otpBankAccountNoError ? 'Bank Account No Required' : ''}
                    value={oneTimePaymentContext.autopayInfo?.otpBankAccountNo || ''}
                    onChange={(e) => {
                      oneTimePaymentContext.dispatchAutopayInfo({
                        type: 'changed_property',
                        property: { otpBankAccountNo: e.target.value },
                      });
                      if (oneTimePaymentContext.otpBankAccountNoError && !isStrEmptyOrNull(e.target.value)) {
                        oneTimePaymentContext.setOtpBankAccountNoError(false);
                      }
                    }}
                  />
                  <div className={Styles.flexRow}>
                    <p className={Styles.helpText}>{'Include leading zeros if present. '}</p>
                    <Button className={Styles.helpButton} onClick={rootContext.scrollToElement}>
                      Help
                    </Button>
                  </div>
                </div>
                <FormControl fullWidth size='small' error={oneTimePaymentContext.otpBankAccountTypeError}>
                  <InputLabel>Bank Account Type *</InputLabel>
                  <Select
                    label='Bank Account Type *'
                    disabled={!fullAccess && isServicerView}
                    value={oneTimePaymentContext.autopayInfo?.otpBankAccountTypeKeyword || ''}
                    onChange={(e) => {
                      oneTimePaymentContext.dispatchAutopayInfo({
                        type: 'changed_property',
                        property: { otpBankAccountTypeKeyword: e.target.value },
                      });
                      if (oneTimePaymentContext.otpBankAccountTypeError) {
                        oneTimePaymentContext.setOtpBankAccountTypeError(false);
                      }
                    }}
                  >
                    {oneTimePaymentContext.otpBankAcctTypeQidInfo.map((option, key) => (
                      <MenuItem key={key} value={option.keyword}>
                        {option.description}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {oneTimePaymentContext.otpBankAccountTypeError ? 'Account Type Required' : ''}
                  </FormHelperText>
                </FormControl>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={oneTimePaymentContext.retainInfo}
                      onChange={() => oneTimePaymentContext.setRetainInfo(!oneTimePaymentContext.retainInfo)}
                    />
                  }
                  className={Styles.controlLabel}
                  label={`${pointerVerb} here to store this account information for your next payment`}
                />
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <FormControl fullWidth>
                    <DatePicker
                      label='Due Date *'
                      disabled
                      slotProps={{
                        textField: {
                          size: 'small',
                        },
                      }}
                      value={
                        oneTimePaymentContext.paymentInfo?.dueDate
                          ? dayjs.unix(oneTimePaymentContext.paymentInfo?.dueDate)
                          : null
                      }
                    />
                  </FormControl>
                </LocalizationProvider>
                <div>
                  <NumericFormat
                    disabled
                    fullWidth
                    size='small'
                    label='Payment *'
                    placeholder='Payment *'
                    inputProps={{ inputMode: 'numeric' }}
                    error={oneTimePaymentContext.paymentError}
                    helperText={oneTimePaymentContext.paymentError ? 'Payment Required' : ''}
                    customInput={TextField}
                    thousandSeparator={true}
                    prefix={'$'}
                    decimalScale={2}
                    fixedDecimalScale={true}
                    allowNegative={false}
                    value={
                      oneTimePaymentContext.removePayment
                        ? oneTimePaymentContext.tempPayment
                        : (oneTimePaymentContext.otpInfo?.payment ?? '')
                    }
                    onValueChange={(values) => {
                      if (oneTimePaymentContext.removePayment) {
                        oneTimePaymentContext.setTempPayment(values.floatValue);
                        if (oneTimePaymentContext.paymentError) {
                          oneTimePaymentContext.setPaymentError(false);
                        }
                      }
                    }}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={oneTimePaymentContext.removePayment}
                        onChange={() => {
                          oneTimePaymentContext.setRemovePayment(!oneTimePaymentContext.removePayment);
                        }}
                      />
                    }
                    className={Styles.controlLabel}
                    label='Check box to remove payment'
                  />
                </div>
                <div />
                <NumericFormat
                  fullWidth
                  disabled={!oneTimePaymentContext.isCurrent || oneTimePaymentContext.currentMonthDueWithoutPayment}
                  size='small'
                  label='Additional Principal'
                  placeholder='Additional Principal'
                  inputProps={{ inputMode: 'numeric' }}
                  value={oneTimePaymentContext.otpInfo?.additionalPrincipal}
                  error={oneTimePaymentContext.additionalPrincipalError}
                  helperText={oneTimePaymentContext.additionalPrincipalError ? 'Additional Principal Required' : ''}
                  customInput={TextField}
                  allowNegative={false}
                  thousandSeparator={true}
                  prefix={'$'}
                  decimalScale={2}
                  fixedDecimalScale={true}
                  onValueChange={(values) => {
                    oneTimePaymentContext.dispatchOtpInfo({
                      type: 'changed_property',
                      property: { additionalPrincipal: values.floatValue },
                    });
                    if (oneTimePaymentContext.additionalPrincipalError) {
                      oneTimePaymentContext.setAdditionalPrincipalError(false);
                    }
                  }}
                />
                <NumericFormat
                  fullWidth
                  size='small'
                  label='Additional Escrow'
                  placeholder='Additional Escrow'
                  inputProps={{ inputMode: 'numeric' }}
                  value={oneTimePaymentContext.otpInfo?.additionalEscrow}
                  error={oneTimePaymentContext.additionalEscrowError}
                  helperText={oneTimePaymentContext.additionalEscrowError ? 'Additional Escrow Required' : ''}
                  customInput={TextField}
                  allowNegative={false}
                  thousandSeparator={true}
                  prefix={'$'}
                  decimalScale={2}
                  fixedDecimalScale={true}
                  isAllowed={(values) => {
                    if (oneTimePaymentContext.choseLumpSum) {
                      if (values.floatValue < Math.abs(oneTimePaymentContext.paymentInfo?.totalEscrowShortage ?? 0)) {
                        return false;
                      }
                    }
                    return true;
                  }}
                  onValueChange={(values) => {
                    oneTimePaymentContext.dispatchOtpInfo({
                      type: 'changed_property',
                      property: { additionalEscrow: values.floatValue },
                    });
                    if (oneTimePaymentContext.additionalEscrowError) {
                      oneTimePaymentContext.setAdditionalEscrowError(false);
                    }
                  }}
                />
              </div>
              <div className={Styles.gridContainer}>
                <NumericFormat
                  fullWidth
                  size='small'
                  label='Late Charges'
                  placeholder='Late Charges'
                  inputProps={{ inputMode: 'numeric' }}
                  value={oneTimePaymentContext.otpInfo?.lateCharges}
                  error={oneTimePaymentContext.lateChargesError || oneTimePaymentContext.lateChargesMaxError}
                  helperText={
                    oneTimePaymentContext.lateChargesError
                      ? 'Late Charges Required'
                      : oneTimePaymentContext.lateChargesMaxError
                        ? `Your maximum late charges balance is ${numericFormatter(
                            (oneTimePaymentContext.loanBalancesInfo?.lcAcrdBal ?? 0).toString(),
                            {
                              thousandSeparator: true,
                              prefix: '$',
                              decimalScale: 2,
                              fixedDecimalScale: true,
                            },
                          )}`
                        : ''
                  }
                  customInput={TextField}
                  allowNegative={false}
                  thousandSeparator={true}
                  prefix={'$'}
                  decimalScale={2}
                  fixedDecimalScale={true}
                  onValueChange={(values) => {
                    oneTimePaymentContext.dispatchOtpInfo({
                      type: 'changed_property',
                      property: { lateCharges: values.floatValue },
                    });
                    if (oneTimePaymentContext.lateChargesError) {
                      oneTimePaymentContext.setLateChargesError(false);
                    }
                    if (oneTimePaymentContext.lateChargesMaxError) {
                      oneTimePaymentContext.setLateChargesMaxError(false);
                    }
                  }}
                />
                <NumericFormat
                  fullWidth
                  size='small'
                  label='Corporate Advance'
                  placeholder='Corporate Advance'
                  inputProps={{ inputMode: 'numeric' }}
                  value={oneTimePaymentContext.otpInfo?.corporateAdvance}
                  error={oneTimePaymentContext.corporateAdvanceError || oneTimePaymentContext.corporateAdvanceMaxError}
                  helperText={
                    oneTimePaymentContext.corporateAdvanceError
                      ? 'Corporate Advance Required'
                      : oneTimePaymentContext.corporateAdvanceMaxError
                        ? `Your maximum corporate advance balance is ${numericFormatter(
                            (-1 * (oneTimePaymentContext.loanBalancesInfo?.corpAdvBal ?? 0)).toString(),
                            {
                              thousandSeparator: true,
                              prefix: '$',
                              decimalScale: 2,
                              fixedDecimalScale: true,
                            },
                          )}`
                        : ''
                  }
                  customInput={TextField}
                  allowNegative={false}
                  thousandSeparator={true}
                  prefix={'$'}
                  decimalScale={2}
                  fixedDecimalScale={true}
                  onValueChange={(values) => {
                    oneTimePaymentContext.dispatchOtpInfo({
                      type: 'changed_property',
                      property: { corporateAdvance: values.floatValue },
                    });
                    if (oneTimePaymentContext.corporateAdvanceError) {
                      oneTimePaymentContext.setCorporateAdvanceError(false);
                    }
                    if (oneTimePaymentContext.corporateAdvanceMaxError) {
                      oneTimePaymentContext.setCorporateAdvanceMaxError(false);
                    }
                  }}
                />
                <NumericFormat
                  fullWidth
                  size='small'
                  label='Assessments'
                  placeholder='Assessments'
                  inputProps={{ inputMode: 'numeric' }}
                  value={oneTimePaymentContext.otpInfo?.assessments}
                  error={oneTimePaymentContext.assessmentsError || oneTimePaymentContext.assessmentsMaxError}
                  helperText={
                    oneTimePaymentContext.assessmentsError
                      ? 'Assessments Required'
                      : oneTimePaymentContext.assessmentsMaxError
                        ? `Your maximum assessments balance is ${numericFormatter(
                            (oneTimePaymentContext.loanBalancesInfo?.assessmentBal ?? 0).toString(),
                            {
                              thousandSeparator: true,
                              prefix: '$',
                              decimalScale: 2,
                              fixedDecimalScale: true,
                            },
                          )}`
                        : ''
                  }
                  customInput={TextField}
                  allowNegative={false}
                  thousandSeparator={true}
                  prefix={'$'}
                  decimalScale={2}
                  fixedDecimalScale={true}
                  onValueChange={(values) => {
                    oneTimePaymentContext.dispatchOtpInfo({
                      type: 'changed_property',
                      property: { assessments: values.floatValue },
                    });
                    if (oneTimePaymentContext.assessmentsError) {
                      oneTimePaymentContext.setAssessmentsError(false);
                    }
                    if (oneTimePaymentContext.assessmentsMaxError) {
                      oneTimePaymentContext.setAssessmentsMaxError(false);
                    }
                  }}
                />
                <NumericFormat
                  fullWidth
                  size='small'
                  label='Suspense'
                  placeholder='Suspense'
                  inputProps={{ inputMode: 'numeric' }}
                  value={oneTimePaymentContext.otpInfo?.suspense}
                  error={oneTimePaymentContext.suspenseError || oneTimePaymentContext.suspenseMaxError}
                  helperText={
                    oneTimePaymentContext.suspenseError
                      ? 'Suspense Required'
                      : oneTimePaymentContext.suspenseMaxError
                        ? `Your maximum suspense balance is ${numericFormatter(
                            (oneTimePaymentContext.loanBalancesInfo?.suspenseBal ?? 0).toString(),
                            {
                              thousandSeparator: true,
                              prefix: '$',
                              decimalScale: 2,
                              fixedDecimalScale: true,
                            },
                          )}`
                        : ''
                  }
                  customInput={TextField}
                  allowNegative={true}
                  thousandSeparator={true}
                  prefix={'$'}
                  decimalScale={2}
                  fixedDecimalScale={true}
                  onValueChange={(values) => {
                    oneTimePaymentContext.dispatchOtpInfo({
                      type: 'changed_property',
                      property: { suspense: values.floatValue },
                    });
                    if (oneTimePaymentContext.suspenseError) {
                      oneTimePaymentContext.setSuspenseError(false);
                    }
                    if (oneTimePaymentContext.suspenseMaxError) {
                      oneTimePaymentContext.setSuspenseMaxError(false);
                    }
                  }}
                />
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <FormControl fullWidth>
                    <DatePicker
                      label='Process Date *'
                      slotProps={{
                        textField: {
                          helperText: oneTimePaymentContext.processDateError
                            ? 'Process Date Must Not Be In The Past'
                            : oneTimePaymentContext.processTomorrowError
                              ? 'You are no longer able to submit payments for the current day, please choose another day.'
                              : '',
                          error: oneTimePaymentContext.processDateError || oneTimePaymentContext.processTomorrowError,
                          size: 'small',
                        },
                      }}
                      minDate={oneTimePaymentContext.minProcessDate()}
                      value={
                        oneTimePaymentContext.otpInfo?.processDate
                          ? dayjs.unix(oneTimePaymentContext.otpInfo?.processDate)
                          : null
                      }
                      onChange={(value) => {
                        if (!value) return;
                        const date =
                          Math.floor(convertDateInputTo8AMUTC(formatDateYYYYMMDD(value.toDate())).getTime()) / 1000;
                        oneTimePaymentContext.dispatchOtpInfo({
                          type: 'changed_property',
                          property: { processDate: date },
                        });
                        if (oneTimePaymentContext.processDateError) {
                          oneTimePaymentContext.setProcessDateError(false);
                        }
                        if (oneTimePaymentContext.processTomorrowError) {
                          oneTimePaymentContext.setProcessTomorrowError(false);
                        }
                      }}
                    />
                  </FormControl>
                </LocalizationProvider>
              </div>
              {isServicerView && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={oneTimePaymentContext.removeProcessingFee}
                      onChange={() => {
                        oneTimePaymentContext.setRemoveProcessingFee(!oneTimePaymentContext.removeProcessingFee);
                      }}
                    />
                  }
                  className={Styles.controlLabel}
                  label='Check box to remove processing fee (if applicable)'
                />
              )}
              <div className={Styles.smallGridContainer}>
                <div className={Styles.feeSummaryRow}>
                  <p className={Styles.summaryLabel}>Processing Fee:</p>
                  {oneTimePaymentContext.otpInfo?.processingFee === 0 ? (
                    <p className={Styles.summaryValue}>Free</p>
                  ) : (
                    <NumericFormat
                      fullWidth
                      size='small'
                      customInput={TextField}
                      thousandSeparator={true}
                      prefix={'$'}
                      decimalScale={2}
                      fixedDecimalScale={true}
                      allowNegative={false}
                      inputProps={{ inputMode: 'numeric' }}
                      error={oneTimePaymentContext.processingFeeError}
                      helperText={oneTimePaymentContext.processingFeeError ? 'Invalid Processing Fee' : ''}
                      value={oneTimePaymentContext.otpInfo?.processingFee ?? ''}
                      onValueChange={(values) => {
                        oneTimePaymentContext.dispatchOtpInfo({
                          type: 'changed_property',
                          property: { processingFee: values.floatValue },
                        });
                        if (oneTimePaymentContext.processingFeeError) {
                          oneTimePaymentContext.setProcessingFeeError(false);
                        }
                      }}
                    />
                  )}
                </div>
                <div className={Styles.feeSummaryRow}>
                  <p className={Styles.summaryLabel}>Total Amount:</p>
                  <p className={Styles.summaryTotalValue}>
                    {numericFormatter(
                      (
                        oneTimePaymentContext.totalWithoutFee + (oneTimePaymentContext.otpInfo?.processingFee ?? 0)
                      ).toString(),
                      {
                        thousandSeparator: true,
                        prefix: '$',
                        decimalScale: 2,
                        fixedDecimalScale: true,
                      },
                    )}
                  </p>
                  <div className={Styles.desktopTotalInput}>
                    <NumericFormat
                      disabled
                      fullWidth
                      size='small'
                      customInput={TextField}
                      thousandSeparator={true}
                      prefix={'$'}
                      decimalScale={2}
                      fixedDecimalScale={true}
                      allowNegative={true}
                      inputProps={{ inputMode: 'numeric' }}
                      error={oneTimePaymentContext.totalError}
                      helperText={oneTimePaymentContext.totalError ? 'Invalid Total' : ''}
                      value={
                        oneTimePaymentContext.totalWithoutFee + (oneTimePaymentContext.otpInfo?.processingFee ?? 0)
                      }
                      onValueChange={(values) => {
                        if (values.floatValue > 0) {
                          oneTimePaymentContext.setTotalError(false);
                        }
                      }}
                    />
                  </div>
                </div>
              </div>
              {isCompactFormLayout && (
                <div className={Styles.mobileActions}>
                  {renderSubmitButton()}
                  {showRemoveBankDetails && renderRemoveBankDetailsButton()}
                </div>
              )}
            </form>
          </div>
        </div>
      </EnvelopeWithBox>
      {!isCompactFormLayout && (
        <ButtonBorder>
          {renderSubmitButton()}
          {showRemoveBankDetails && renderRemoveBankDetailsButton()}
        </ButtonBorder>
      )}
    </ButtonBorderWrapper>
  );
};

export default OneTimePaymentForm;
